import { PortfolioAccountData } from "@shared/features/portfolio/types";
import Card from "common/Boxes/Card";
import Text from "common/Typography/Text";
import HorizontalPieChart from "features/portfolio/Accounts/HorizontalPieChart";
import { selectPortfolioPrecision } from "@shared/slices/portfolio.slice";
import React, { ReactNode } from "react";
import { useSelector } from "react-redux";
import { DISPLAY_CURRENCIES, ICON_TYPES } from "@shared/common/constants";
import IconWithFallback from "Global/IconWithFallback";
import Flex from "common/Boxes/Flex";
import styled from "styled-components";
import { typography } from "@shared/common/Themes/typography";
import AccountActions from "features/configuration/accounts/AccountActions";
import AccountStatusTag from "features/configuration/accounts/AccountConfigurationDrawer/AccountStatusTag";
import WhitelistTag from "features/configuration/accounts/AccountConfigurationDrawer/WhitelistTag";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  ClientStatus,
  ConsolidatedAccountData,
  LanguageNameSpaces,
  PrecisionType,
} from "@shared/types";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import AccountTableChart from "features/portfolio/Accounts/AccountTableChart";
import ChartSkeleton from "features/portfolio/Accounts/ChartSkeleton";
import LineSkeleton from "common/Skeleton/LineSkeleton";
import { useAccountCard } from "@shared/features/portfolio/hooks";
import Tooltip from "common/Tooltips/Tooltip";
import { MOBILE_LAYOUT_ONE_COLUMN } from "common/Layouts/TwoChartsTableLayout.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface AccountCardProps {
  account: PortfolioAccountData;
  loading?: boolean;
  openAccountExtraDetails?: (accountData: ConsolidatedAccountData) => void;
}

const AccountCard = ({
  account,
  loading,
  openAccountExtraDetails,
}: AccountCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { accountCoins } = useAccountCard({ account });
  const isInvalid = account.client_status !== ClientStatus.ACTIVE;

  const getDataRow = (name: string, value: ReactNode) => {
    return (
      <DataRow>
        <Text type="secondary">{name}</Text>
        <Text as="div" $weight={typography.fontWeights.MEDIUM}>
          {value}
        </Text>
      </DataRow>
    );
  };

  const getAmountText = (value: number) => {
    return (
      <TextWithPrecisionAndIcon
        precisionSelector={selectPortfolioPrecision}
        precisionType={PrecisionType.AMOUNT}
        text={value}
        round={true}
      />
    );
  };

  const getBalanceRow = () => {
    const style = { maxWidth: BALANCE_CHART_WIDTH };
    let chart: ReactNode = <ChartSkeleton style={style} active={false} />;
    if (!loading && !isInvalid) {
      chart = account.graph_data ? (
        <AccountTableChart data={account.graph_data} style={style} />
      ) : undefined;
    }
    return (
      <Flex>
        <Text type="secondary">{t("accounts.balance_24h")}</Text>
        {chart}
      </Flex>
    );
  };

  return (
    <StyledCard onClick={() => openAccountExtraDetails?.(account)}>
      <TopRowWrapper>
        <IconWithFallback
          iconType={ICON_TYPES.EXCHANGE}
          coinOrExchangeName={account.exchange}
          iconStyle={{
            width: 50,
            height: 50,
            marginRight: 12,
            borderRadius: 6,
          }}
        />
        <div style={{ paddingRight: 30, overflow: "hidden" }}>
          <Tooltip title={account.client_name}>
            <Text
              $weight={typography.fontWeights.SEMI_BOLD}
              $size="L"
              style={{ marginBottom: 4 }}
              $ellipsis={true}
            >
              {account.client_name}
            </Text>
          </Tooltip>
          <Text type="secondary" $size="S" $ellipsis={true}>
            {account.exchange}
          </Text>
        </div>
        <ActionsWrapper onClick={(e) => e.stopPropagation()}>
          <AccountActions accountId={account.client_id} iconSize={24} />
        </ActionsWrapper>
      </TopRowWrapper>
      <HorizontalPieChart
        items={accountCoins}
        id={account.client_id.toString()}
        showSkeleton={loading || isInvalid}
        skeletonActive={loading}
        getLegendExtraData={(coinData) => {
          return [
            {
              name: t("accounts.total"),
              value: getAmountText(
                displayCurrency === DISPLAY_CURRENCIES.BTC
                  ? coinData.total_in_btc
                  : coinData.total_in_usd
              ),
            },
            {
              name: t("accounts.available"),
              value: getAmountText(
                displayCurrency === DISPLAY_CURRENCIES.BTC
                  ? coinData.available_in_btc
                  : coinData.available_in_usd
              ),
            },
          ];
        }}
      />
      <DataRowsWrapper>
        {getDataRow(
          t("accounts.status"),
          <Flex
            $flexDirection="column"
            $alignItems="flex-end"
            style={{ rowGap: 6 }}
          >
            {account.client_status && (
              <AccountStatusTag
                style={{ width: "fit-content" }}
                status={account.client_status}
                withBgColor={true}
              />
            )}
            {account.has_assigned_ips === false && account.is_valid && (
              <WhitelistTag
                withBgColor={false}
                animated={false}
                exchange={account.exchange}
              />
            )}
          </Flex>
        )}
        {getDataRow(
          t("accounts.total"),
          loading ? (
            <LineSkeleton style={{ width: 75 }} />
          ) : isInvalid ? (
            t("common.n_a")
          ) : (
            getAmountText(
              displayCurrency === DISPLAY_CURRENCIES.BTC
                ? account.total_in_btc
                : account.total_in_usd
            )
          )
        )}
        {getDataRow(
          t("accounts.available"),
          loading ? (
            <LineSkeleton style={{ width: 65 }} />
          ) : isInvalid ? (
            t("common.n_a")
          ) : (
            getAmountText(
              displayCurrency === DISPLAY_CURRENCIES.BTC
                ? account.available_in_btc
                : account.available_in_usd
            )
          )
        )}
        {getBalanceRow()}
      </DataRowsWrapper>
    </StyledCard>
  );
};

export default React.memo(AccountCard);

const BALANCE_CHART_WIDTH = 160;

const StyledCard = styled(Card)`
  cursor: pointer;
  overflow: hidden;
  @media (min-width: ${MOBILE_LAYOUT_ONE_COLUMN}) {
    background: ${(p) => p.theme.colors.secondaryCardBgColor};
  }
`;
const TopRowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-bottom: 8px;
  border-bottom: ${(p) => p.theme.borders.lightest};
  margin-bottom: 8px;
  position: relative;
`;
const ActionsWrapper = styled.div`
  position: absolute;
  right: -4px;
  top: -4px;
  rotate: 90deg;
`;
const DataRowsWrapper = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`;
const DataRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 17px;
`;
